/**
 * useStructuredData — injects JSON-LD structured data into the page <head>
 * for enhanced AI search visibility (GEO) and rich results.
 *
 * Supports: Organization, LocalBusiness, WebSite, Service, FAQPage,
 * HowTo, BreadcrumbList, Offer
 * Supports AggregateRating based on verified third-party Google reviews
 */
import { useEffect } from "react";
import { useLocation } from "wouter";

const SITE_URL = "https://fittedwardrobes.com";
const LOGO_URL = "https://fittedwardrobes.com/manus-storage/smiths-fittedwardrobes-logo_c3b2adff.svg";
const HERO_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663028452210/atDbSHDNPqxhxuRpaHVqg4/hero-new-q50_1cc4d912.webp";

// ─── Shared entity fragments ───────────────────────────────────────────────

const organizationEntity = {
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "Smiths Fitted Wardrobes & Kitchens Ltd",
  url: SITE_URL,
  logo: { "@type": "ImageObject", url: LOGO_URL },
  telephone: "+448002922200",
  email: "info@fittedwardrobes.com",
  foundingDate: "2012",
  numberOfEmployees: { "@type": "QuantitativeValue", minValue: 20 },
  sameAs: [
    "https://www.facebook.com/SmithsFittedWardrobes",
    "https://www.instagram.com/smithsfittedwardrobes",
    "https://www.pinterest.co.uk/smithsfittedwardrobes",
    "https://www.youtube.com/@smithsfittedwardrobes",
    "https://www.tiktok.com/@smithsfittedwardrobes",
    "https://www.google.com/maps/place/Smiths+Fitted+Wardrobes",
    "https://www.houzz.co.uk/pro/smithsfittedwardrobes",
    "https://www.checkatrade.com/trades/smithsfittedwardrobeskitchenslimited",
    "https://uk.trustpilot.com/review/www.fittedwardrobes.com",
    "https://trustedtraders.which.co.uk/businesses/smiths-fitted-wardrobes-kitchens-ltd/",
    "https://www.reviews.io/company-reviews/store/smiths-fitted-wardrobes-ltd-",
    "https://www.homify.co.uk/professionals/5728/smiths-fitted-wardrobes-ltd",
  ],
};

const localBusinessEntity = {
  "@type": "HomeAndConstructionBusiness",
  "@id": `${SITE_URL}/#localbusiness`,
  name: "Smiths Fitted Wardrobes & Kitchens Ltd",
  description:
    "London-based bespoke fitted furniture company established in 2012. Designs, manufactures and installs custom-made fitted wardrobes, sliding wardrobes, walk-in wardrobes, fitted bedrooms, fitted kitchens, media wall units, home offices, loft wardrobes and under-stairs storage. All furniture handcrafted in their London factory. 10-year guarantee, free 3D design, fitted in 3 weeks. Which? Trusted Trader.",
  url: SITE_URL,
  telephone: "+448002922200",
  email: "info@fittedwardrobes.com",
  image: HERO_IMG,
  logo: LOGO_URL,
  priceRange: "£1,999 - £24,000",
  currenciesAccepted: "GBP",
  paymentAccepted: "Cash, Credit Card, Bank Transfer",
  address: {
    "@type": "PostalAddress",
    streetAddress: "143 Floor 1, Stockley Park, 4 Longwalk Road",
    addressLocality: "Uxbridge",
    addressRegion: "Greater London",
    postalCode: "UB11 1FE",
    addressCountry: "GB",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 51.5117,
    longitude: -0.4811,
  },
  areaServed: { "@type": "Country", name: "England", sameAs: "https://en.wikipedia.org/wiki/England" },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "09:00",
      closes: "18:00",
    },
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Bespoke Fitted Furniture Services",
    itemListElement: [
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Fitted Wardrobes" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Sliding Wardrobes" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Walk-In Wardrobes" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Fitted Bedrooms" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Fitted Kitchens" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Media Wall Units" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Home Office Furniture" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Loft Wardrobes" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Under Stairs Storage" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Kitchen Worktops" } },
    ],
  },
  sameAs: [
    "https://www.facebook.com/SmithsFittedWardrobes",
    "https://www.instagram.com/smithsfittedwardrobes",
    "https://www.pinterest.co.uk/smithsfittedwardrobes",
    "https://www.youtube.com/@smithsfittedwardrobes",
    "https://www.tiktok.com/@smithsfittedwardrobes",
    "https://www.google.com/maps/place/Smiths+Fitted+Wardrobes",
    "https://www.houzz.co.uk/pro/smithsfittedwardrobes",
    "https://www.checkatrade.com/trades/smithsfittedwardrobeskitchenslimited",
    "https://uk.trustpilot.com/review/www.fittedwardrobes.com",
    "https://trustedtraders.which.co.uk/businesses/smiths-fitted-wardrobes-kitchens-ltd/",
    "https://www.reviews.io/company-reviews/store/smiths-fitted-wardrobes-ltd-",
    "https://www.homify.co.uk/professionals/5728/smiths-fitted-wardrobes-ltd",
  ],
  knowsAbout: [
    "Fitted wardrobes",
    "Sliding wardrobes",
    "Walk-in wardrobes",
    "Fitted bedrooms",
    "Fitted kitchens",
    "Media wall units",
    "Home office furniture",
    "Loft conversions storage",
    "Under stairs storage",
    "Bespoke furniture design",
    "3D furniture visualisation",
    "MDF wardrobe construction",
    "Egger MFC carcass materials",
    "Blum soft-close hardware",
    "Handleless wardrobe doors",
    "LED wardrobe lighting",
    "Interior design for bedrooms",
    "Space-saving storage solutions",
    "Sloped ceiling wardrobes",
    "Alcove fitted furniture",
  ],
  // AggregateRating REMOVED — Google's review-snippet guidelines state that
  // LocalBusiness/HomeAndConstructionBusiness types are NOT eligible for review
  // snippet rich results when the business is rating itself ("self-serving").
  // This was causing: "Invalid object type for field" error in Search Console.
  // See: https://developers.google.com/search/docs/appearance/structured-data/review-snippet
};

// ─── Product schema for AI categorization ─────────────────────────────────

const productEntities = [
  {
    "@type": "Product",
    "@id": `${SITE_URL}/#product-fitted-wardrobes`,
    name: "Bespoke Fitted Wardrobes",
    description:
      "Custom-made, floor-to-ceiling fitted wardrobes designed, manufactured in London, and professionally installed. Over 50 door finishes, soft-close Blum hinges, integrated LED lighting, and 10-year guarantee included. Made to measure for any room shape including sloped ceilings, alcoves, and chimney breasts.",
    image: HERO_IMG,
    brand: { "@type": "Brand", name: "Smiths Fitted Wardrobes & Kitchens" },
    manufacturer: { "@id": `${SITE_URL}/#organization` },
    category: "Fitted Furniture",
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "GBP",
      lowPrice: "1200",
      highPrice: "4800",
      offerCount: "50",
      availability: "https://schema.org/InStock",
      seller: { "@id": `${SITE_URL}/#localbusiness` },
      priceValidUntil: "2026-12-31",
    },
  },
  {
    "@type": "Product",
    "@id": `${SITE_URL}/#product-sliding-wardrobes`,
    name: "Sliding Wardrobes",
    description:
      "Space-saving sliding wardrobe systems with mirrored glass, coloured glass, or solid panel doors. Bottom-rolling precision track systems. 2, 3, or 4 panel configurations in aluminium frames. Made to measure with 10-year guarantee.",
    image: HERO_IMG,
    brand: { "@type": "Brand", name: "Smiths Fitted Wardrobes & Kitchens" },
    manufacturer: { "@id": `${SITE_URL}/#organization` },
    category: "Fitted Furniture",
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "GBP",
      lowPrice: "1300",
      highPrice: "4800",
      offerCount: "30",
      availability: "https://schema.org/InStock",
      seller: { "@id": `${SITE_URL}/#localbusiness` },
      priceValidUntil: "2026-12-31",
    },
  },
  {
    "@type": "Product",
    "@id": `${SITE_URL}/#product-walk-in-wardrobes`,
    name: "Walk-In Wardrobes",
    description:
      "Luxury bespoke walk-in wardrobes and dressing rooms with island units, full-length mirrors, LED lighting, shoe racks, and jewellery drawers. Designed and built to transform your space into a boutique-style dressing area.",
    image: HERO_IMG,
    brand: { "@type": "Brand", name: "Smiths Fitted Wardrobes & Kitchens" },
    manufacturer: { "@id": `${SITE_URL}/#organization` },
    category: "Fitted Furniture",
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "GBP",
      lowPrice: "2400",
      highPrice: "6000",
      offerCount: "20",
      availability: "https://schema.org/InStock",
      seller: { "@id": `${SITE_URL}/#localbusiness` },
      priceValidUntil: "2026-12-31",
    },
  },
  {
    "@type": "Product",
    "@id": `${SITE_URL}/#product-fitted-kitchens`,
    name: "Fitted Kitchens",
    description:
      "Custom-designed fitted kitchens with premium Egger, Xylo, and Cleaf materials. Quartz and granite worktops, integrated appliances, full project management from design to installation including plumbing and electrical.",
    image: HERO_IMG,
    brand: { "@type": "Brand", name: "Smiths Fitted Wardrobes & Kitchens" },
    manufacturer: { "@id": `${SITE_URL}/#organization` },
    category: "Fitted Kitchens",
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "GBP",
      lowPrice: "6000",
      highPrice: "24000",
      offerCount: "15",
      availability: "https://schema.org/InStock",
      seller: { "@id": `${SITE_URL}/#localbusiness` },
      priceValidUntil: "2026-12-31",
    },
  },
  {
    "@type": "Product",
    "@id": `${SITE_URL}/#product-media-walls`,
    name: "Media Wall Units",
    description:
      "Bespoke media wall entertainment centres with recessed TV alcoves, ambient LED lighting, electric fireplace integration, cable management, and display shelving.",
    image: HERO_IMG,
    brand: { "@type": "Brand", name: "Smiths Fitted Wardrobes & Kitchens" },
    manufacturer: { "@id": `${SITE_URL}/#organization` },
    category: "Fitted Furniture",
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "GBP",
      lowPrice: "2400",
      highPrice: "7000",
      offerCount: "25",
      availability: "https://schema.org/InStock",
      seller: { "@id": `${SITE_URL}/#localbusiness` },
      priceValidUntil: "2026-12-31",
    },
  },
];

const websiteEntity = {
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: "Smiths Fitted Wardrobes & Kitchens",
  url: SITE_URL,
  publisher: { "@id": `${SITE_URL}/#organization` },
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}/area-we-cover/?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

// ─── Service definitions ───────────────────────────────────────────────────

interface ServiceDef {
  name: string;
  description: string;
  url: string;
  image?: string;
  priceRange?: string;
}

const SERVICES: ServiceDef[] = [
  {
    name: "Fitted Wardrobes London",
    description:
      "Bespoke floor-to-ceiling fitted wardrobes made to measure in London. Over 20 door styles, 50+ colours, soft-close hinges, integrated LED lighting. Handcrafted in our London factory with a 10-year guarantee. Prices from £1,499 after discounts. Free design visit and 3D visualisation included.",
    url: `${SITE_URL}/fitted-wardrobes/`,
    priceRange: "£1,499 - £3,999",
  },
  {
    name: "Sliding Wardrobes London",
    description:
      "Space-saving sliding wardrobe systems with mirrored glass, coloured glass or solid panel doors. Bottom-rolling precision tracks for smooth operation. 2, 3 or 4 panel configurations. Aluminium frames in silver, white, black or bronze. Made to measure with 10-year guarantee.",
    url: `${SITE_URL}/sliding-wardrobes/`,
    priceRange: "£1,999 - £3,999",
  },
  {
    name: "Walk-In Wardrobes London",
    description:
      "Luxury bespoke walk-in wardrobes and dressing rooms with island units, full-length mirrors, LED lighting, shoe racks and jewellery drawers. Designed and built to transform your space into a boutique-style dressing area. 10-year guarantee included.",
    url: `${SITE_URL}/walk-in-wardrobes/`,
    priceRange: "£1,299 - £8,000",
  },
  {
    name: "Fitted Bedrooms London",
    description:
      "Complete bespoke fitted bedroom furniture packages including wardrobes, bedside tables, chest of drawers, dressing tables and headboards. All manufactured from matching materials for a seamless designer look. 10-year guarantee.",
    url: `${SITE_URL}/fitted-bedrooms/`,
    priceRange: "£1,999 - £9,000",
  },
  {
    name: "Fitted Kitchens London",
    description:
      "Custom-designed fitted kitchens crafted in-house using premium Egger, Xylo and Cleaf materials, quartz and granite worktops, integrated appliances. Full project management from design to installation including plumbing and electrical.",
    url: `${SITE_URL}/fitted-kitchens/`,
    priceRange: "£6,000 - £24,000",
  },
  {
    name: "Media Wall Units London",
    description:
      "Bespoke media wall entertainment centres with recessed TV alcoves, ambient LED lighting, electric fireplace integration, cable management and display shelving. One of London's fastest-growing fitted furniture categories.",
    url: `${SITE_URL}/media-wall-units/`,
    priceRange: "£1,999 - £7,000",
  },
  {
    name: "Home Office Furniture London",
    description:
      "Bespoke fitted home office furniture with built-in desks, shelving, filing cabinets and cable management. L-shaped, straight and corner configurations. Designed for productive home working.",
    url: `${SITE_URL}/home-office/`,
    priceRange: "£1,999 - £4,000",
  },
  {
    name: "Loft Wardrobes London",
    description:
      "Specialist loft conversion and attic wardrobes designed to maximise awkward sloped ceiling spaces. Laser-measured to capture exact roof angles. Knee-wall storage, dormer window seats and eaves access hatches.",
    url: `${SITE_URL}/loft-wardrobes/`,
    priceRange: "£1,999 - £4,999",
  },
  {
    name: "Under Stairs Storage London",
    description:
      "Custom under-stairs storage solutions transforming dead space into functional pull-out drawers, wine racks, coat cupboards, home office nooks and shelving. Designed to match the exact staircase profile.",
    url: `${SITE_URL}/under-stairs/`,
    priceRange: "£1,999 - £4,000",
  },
  {
    name: "Kitchen Worktops London",
    description:
      "Premium stone kitchen worktops in Quartz, Granite, Marble, Porcelain, Quartzite and Dekton. Supply and installation across London and Home Counties. Interactive 4-step online quote builder with instant pricing. Brands include Silestone, Caesarstone, Compac, Cosentino. From £160/m².",
    url: `${SITE_URL}/kitchen-worktops/`,
    priceRange: "£160 - £250 per m²",
  },
];

// ─── Service page FAQ definitions (from serviceData.ts) ──────────────────

const SERVICE_PAGE_FAQS: Record<string, { question: string; answer: string }[]> = {
  "fitted-wardrobes": [
    { question: "How much do fitted wardrobes cost?", answer: "Fitted wardrobe prices start from £1,499 for a hinged door wardrobe (typical spend £1,499–£3,999 after discounts) and from £1,999 for sliding door wardrobes (typical spend £1,999–£3,999 after discounts). Based on an average-sized room — larger spaces may cost more. All prices include design, manufacturing, delivery and professional installation." },
    { question: "How long does it take to fit a wardrobe?", answer: "From your design approval, manufacturing takes approximately 2–3 weeks. Installation typically takes 1–2 days depending on the size and complexity of the project." },
    { question: "Can you fit wardrobes around chimney breasts and alcoves?", answer: "Absolutely — that is where bespoke fitted wardrobes excel. We design around chimney breasts, alcoves, sloped ceilings, and any other architectural features to maximise every inch of space." },
  ],
  "fitted-bedrooms": [
    { question: "What is included in a fitted bedroom package?", answer: "A typical fitted bedroom includes wardrobes, bedside cabinets, a dressing table with mirror, chest of drawers, and an optional headboard surround. Every piece is designed to match perfectly." },
    { question: "Can you design a fitted bedroom for a small room?", answer: "Yes — bespoke fitted bedrooms are ideal for small rooms because every piece is made to measure. We maximise storage while maintaining a spacious feel." },
    { question: "How much does a fitted bedroom cost?", answer: "A complete fitted bedroom typically costs from £1,999 (typical spend £1,999–£9,000 after discounts) depending on room size, number of pieces, and materials chosen. Based on an average-sized room — larger spaces may cost more. We provide a detailed quote during your free design visit." },
  ],
  "sliding-wardrobes": [
    { question: "Are sliding wardrobes good for small bedrooms?", answer: "Sliding wardrobes are ideal for small bedrooms because the doors do not swing outward, requiring zero clearance space. This means you can place furniture closer to the wardrobe." },
    { question: "Do sliding wardrobe doors have a floor track?", answer: "Our premium sliding systems use bottom-rolling precision tracks for smooth, reliable operation. The doors glide effortlessly with a soft-close mechanism for silent, gentle stops." },
    { question: "How wide can sliding wardrobes be?", answer: "Our sliding wardrobes can span up to 4 metres wide with 2, 3, or 4 door configurations. For wider walls, we can design multiple units that sit side by side for a seamless appearance." },
  ],
  "walk-in-wardrobes": [
    { question: "How much space do I need for a walk-in wardrobe?", answer: "A functional walk-in wardrobe can work in a space as small as 2m x 1.5m. For a more luxurious dressing room with a central island, we recommend at least 3m x 3m." },
    { question: "Can I convert a spare bedroom into a walk-in wardrobe?", answer: "Yes — converting a spare bedroom into a walk-in wardrobe is one of our most popular projects. We design the layout to maximise storage while maintaining a spacious, boutique feel." },
    { question: "Do walk-in wardrobes add value to a home?", answer: "Yes — a well-designed walk-in wardrobe is a highly desirable feature that can add significant value to a property." },
  ],
  "loft-wardrobes": [
    { question: "Can you fit wardrobes under sloped ceilings?", answer: "Yes — this is our speciality. We design and build wardrobes that follow the exact angle of your roofline, maximising storage in spaces that standard furniture cannot fill." },
    { question: "What about the low eaves areas?", answer: "We design clever eaves storage solutions with pull-out drawers, shoe racks, and low shelving that make use of areas too low for hanging rails. Nothing is wasted." },
    { question: "Do you work around loft obstacles like water tanks?", answer: "Yes — our experienced fitters work carefully around roof timbers, water tanks, electrical runs, and other loft obstacles." },
  ],
  "media-wall-units": [
    { question: "Can you integrate a fireplace into a media wall?", answer: "Yes — integrated electric fireplaces are one of our most popular media wall features. We work with leading brands to offer linear, log-effect, and crystal-effect fires." },
    { question: "How do you handle TV cables and equipment?", answer: "Every media wall includes hidden cable channels routed from the TV alcove to equipment storage. Ventilated closed cupboards house your Sky box, games console, and other devices out of sight." },
    { question: "How much does a media wall cost?", answer: "Media wall units typically cost from £1,999 (typical spend £1,999–£7,000 after discounts) depending on size, features, and whether an integrated fireplace is included. Based on an average-sized room — larger spaces may cost more." },
  ],
  "home-office": [
    { question: "Can you fit a home office in a small space?", answer: "Yes — we specialise in designing home offices for compact spaces including alcoves, under stairs areas, and small spare rooms." },
    { question: "Do you include cable management?", answer: "Yes — every home office includes integrated cable management with hidden channels for power cables, USB hubs, and network connections." },
    { question: "Can you design a dual workstation for two people?", answer: "Absolutely — we design dual workstation home offices with separate desk areas, individual storage, and shared shelving." },
  ],
  "under-stairs": [
    { question: "What can I store under my stairs?", answer: "Almost anything — popular options include shoes and coats, vacuum cleaners and cleaning supplies, wine collections, children's toys, and even a compact home office." },
    { question: "Can you match the doors to my existing hallway?", answer: "Yes — we can match the door style, colour, and handles to your existing hallway decor for a seamless look." },
    { question: "How much does under stairs storage cost?", answer: "Under stairs storage typically costs from £1,999 (typical spend £1,999–£4,000 after discounts) depending on the size of the space and the complexity of the design." },
  ],
  "fitted-kitchens": [
    { question: "How long does a kitchen installation take?", answer: "A typical kitchen installation takes 5–10 working days depending on the size and complexity. This includes removal of the old kitchen, installation of new units, worktop templating and fitting, and plumbing and electrical connections." },
    { question: "Do you supply and fit appliances?", answer: "Yes — we can supply and integrate appliances from leading brands including Bosch, Siemens, Neff, and AEG." },
    { question: "What worktop options do you offer?", answer: "We offer quartz (Silestone, Caesarstone), granite, solid wood (oak, walnut), and premium laminate worktops. Quartz is our most popular choice." },
  ],
  "kitchen-worktops": [
    { question: "What kitchen worktop materials do you offer?", answer: "We offer six premium materials: Quartz (from £180/m²), Granite (from £160/m²), Marble (from £200/m²), Porcelain (from £220/m²), Quartzite (from £250/m²), and Dekton (from £240/m²). Each material has unique properties suited to different kitchen styles and usage requirements." },
    { question: "How do I get a quote for kitchen worktops?", answer: "Use our interactive 4-step online quote builder at fittedwardrobes.com/kitchen-worktops. Select your material, choose your kitchen layout and enter dimensions, pick extras like sink cutouts and edge profiles, then submit your details for an instant quote." },
    { question: "What is the best kitchen worktop material?", answer: "Quartz is our most popular choice — it is non-porous, scratch-resistant, low maintenance, and available in hundreds of colours and patterns. For a natural stone look, granite and marble are excellent choices. Dekton is virtually indestructible and ideal for heavy-use kitchens." },
    { question: "Do you supply and fit kitchen worktops?", answer: "Yes — we handle the entire process from templating to fabrication and installation. Our experienced fitters ensure a perfect fit with precise cutouts for sinks, hobs, and taps." },
    { question: "How long does worktop installation take?", answer: "After templating (which takes about 1 hour), fabrication takes 5-7 working days. Installation is typically completed in half a day for a standard kitchen." },
  ],
};

// ─── Area Page FAQs (generic template — applies to all area pages) ────────────
// These are location-relevant questions that apply universally to all service areas.
// The visible FAQ section on AreaPage.tsx renders these same questions with the area name interpolated.
const AREA_PAGE_FAQ_TEMPLATE: { question: string; answer: string }[] = [
  { question: "How much do fitted wardrobes cost in {area}?", answer: "Fitted wardrobe prices in {area} start from £1,499 for hinged door wardrobes and from £1,999 for sliding door wardrobes, fully installed including VAT. Final cost depends on room size, materials, and internal configuration. We provide a fixed, itemised quote during your free design visit." },
  { question: "How quickly can you fit wardrobes in {area}?", answer: "We can typically complete your fitted wardrobes within 3 weeks of design approval. Our London factory and efficient logistics mean faster turnaround than most national competitors. Installation itself usually takes 1–2 days depending on complexity." },
  { question: "Do you offer a free design visit in {area}?", answer: "Yes — we offer a completely free, no-obligation design visit to your home in {area}. Our designer will measure your space, discuss your requirements, and create a 3D visualisation of your new wardrobes. You'll receive a fixed quote on the same day." },
  { question: "What guarantee do you offer on fitted wardrobes in {area}?", answer: "All our fitted furniture comes with a comprehensive 10-year guarantee covering both materials and workmanship. We use premium Egger and Cleaf boards with Blum soft-close hardware, and our wardrobes typically last 20+ years." },
  { question: "Can you work with awkward spaces like lofts and alcoves in {area}?", answer: "Absolutely — bespoke fitted wardrobes are ideal for challenging spaces. We regularly work with sloped ceilings, chimney breasts, alcoves, and under-eaves areas. Every piece is made to measure in our London factory to fit your exact space." },
];

// Generate area-specific FAQs by interpolating the area name
const AREA_PAGE_FAQS: Record<string, { question: string; answer: string }[]> = new Proxy({} as any, {
  get(_target, prop: string) {
    // Convert slug to display name (e.g. "richmond" -> "Richmond", "staines-upon-thames" -> "Staines-upon-Thames")
    const areaName = prop.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ").replace(/ Upon /g, "-upon-").replace(/ On /g, "-on-");
    return AREA_PAGE_FAQ_TEMPLATE.map(f => ({
      question: f.question.replace(/{area}/g, areaName),
      answer: f.answer.replace(/{area}/g, areaName),
    }));
  },
  has() { return true; }
});

// ─── FAQ definitions ───────────────────────────────────────────────────────

const HOMEPAGE_FAQS = [
  {
    question: "How long does the whole process take from consultation to installation?",
    answer:
      "Typically 3-4 weeks from your free design visit to completed installation. We work efficiently to minimise disruption to your home and daily life.",
  },
  {
    question: "Do you offer a guarantee on your fitted furniture?",
    answer:
      "Yes! We offer a comprehensive 10-year guarantee on all our fitted furniture. This covers both materials and workmanship, giving you complete peace of mind.",
  },
  {
    question: "Can you work with unusual room shapes or awkward spaces?",
    answer:
      "Absolutely — that's where we excel! Our bespoke approach means we can work with sloped ceilings, alcoves, chimney breasts, and any other challenging spaces. Every piece is made to measure.",
  },
  {
    question: "What areas do you cover?",
    answer:
      "We cover all of London and the surrounding areas including Surrey, Kent, Essex, Hertfordshire, Buckinghamshire, and Berkshire. Contact us to confirm coverage in your area.",
  },
  {
    question: "How much does fitted furniture cost?",
    answer:
      "Pricing depends on the size, style, and materials chosen. We offer competitive pricing and flexible payment options. All prices include VAT and professional installation. Our free design visit includes a detailed, no-obligation quote. We also offer a price-match promise on comparable quotes.",
  },
  {
    question: "Can I see your work before committing?",
    answer:
      "Yes! Browse our recent work at fittedwardrobes.com/recent-work, or ask to see examples of completed projects near you. We're also happy to provide references from satisfied customers. We have over 950+ reviews across 7 platforms with a 4.7 average rating.",
  },
  {
    question: "How does Smiths compare to Sharps or Hammonds?",
    answer:
      "Smiths offers comparable or superior quality at competitive prices. We manufacture in our own London factory (not imported flat-pack), use directly employed craftsmen (no subcontractors), and offer a price-match promise on comparable quotes. We also provide a 10-year guarantee and free 3D design service.",
  },
  {
    question: "Do you offer finance options?",
    answer:
      "Yes, we offer flexible payment options to suit your budget. Ask our team for details during your free design visit.",
  },
];

const WHY_US_FAQS = [
  ...HOMEPAGE_FAQS,
  {
    question: "What happens if I'm not happy with the design?",
    answer:
      "We work with you to refine the design until you're 100% happy. You'll receive unlimited design revisions at no extra cost. We don't start manufacturing until you've approved every detail.",
  },
  {
    question: "Are your fitters employed directly or subcontractors?",
    answer:
      "All our fitters are directly employed by Smiths — we never use subcontractors. This ensures consistent quality, accountability, and the high standards our customers expect.",
  },
  {
    question: "What materials do you use?",
    answer:
      "We use premium materials including moisture-resistant MDF, 18mm thick carcasses, soft-close Blum hinges and drawer runners, and a choice of over 50 door finishes including matt, gloss, woodgrain and hand-painted options.",
  },
  {
    question: "Can I visit a showroom?",
    answer:
      "Yes, showroom visits are available by appointment. Contact us on 0800 292 2200 to arrange a visit. Alternatively, browse our recent work or book a free home design visit.",
  },
];

// ─── Answers Hub FAQs (imported from data file for structured data) ──────────────────
import { answersPages } from "@/data/answersData";
const ANSWERS_FAQS: Record<string, { question: string; answer: string }[]> = Object.fromEntries(
  answersPages.map((p) => [p.slug, p.faqs])
);

// ─── Review definitions (REMOVED: self-serving reviews not eligible for Google rich results) ──
// Google's review snippet documentation states that LocalBusiness and Organization
// reviews are only eligible for sites that review OTHER businesses/organizations.
// See: https://developers.google.com/search/docs/appearance/structured-data/review-snippet

/* REMOVED: Self-serving reviews cause "Invalid object type for field parent_node" error
const REVIEWS = [
  {
    author: "Sarah Mitchell",
    datePublished: "2026-03-20",
    reviewRating: 5,
    reviewBody:
      "Absolutely stunning result! The team transformed our master bedroom with the most beautiful fitted wardrobes. The quality is exceptional and the attention to detail is second to none. Highly recommend!",
  },
  {
    author: "James Thompson",
    datePublished: "2026-03-01",
    reviewRating: 5,
    reviewBody:
      "Our walk-in wardrobe is now our favourite room in the house. The 3D design process was brilliant — we could see exactly what we were getting before they built it. Worth every penny!",
  },
  {
    author: "Emma Kelly",
    datePublished: "2026-03-10",
    reviewRating: 5,
    reviewBody:
      "The media wall they installed in our living room is absolutely spectacular. Friends and family can't stop commenting on it. Professional team, clean installation, and a beautiful result.",
  },
  {
    author: "David Roberts",
    datePublished: "2026-02-15",
    reviewRating: 5,
    reviewBody:
      "We had our entire bedroom and home office fitted. The quality of materials and craftsmanship is outstanding. The 3D design process helped us visualise exactly what we wanted. Brilliant service!",
  },
  {
    author: "Lisa Anderson",
    datePublished: "2026-03-25",
    reviewRating: 5,
    reviewBody:
      "Best decision we made for our home. The fitted wardrobes have completely transformed our bedroom and given us so much more storage. The 10-year guarantee gives us complete peace of mind.",
  },
  {
    author: "Michael Brown",
    datePublished: "2026-02-01",
    reviewRating: 5,
    reviewBody:
      "Exceptional quality and service. We've used Smiths twice now — first for our bedroom and then for our kitchen. Both times the result exceeded our expectations. You won't find better!",
  },
];
*/

// ─── HowTo steps ───────────────────────────────────────────────────────────

const HOW_TO_STEPS = [
  {
    name: "Book a Free Design Visit",
    text: "Call 0800 292 2200 or book online. An expert designer visits your home, takes precise measurements, discusses your requirements and shows you material samples. No obligation.",
    url: `${SITE_URL}/contact/`,
  },
  {
    name: "Receive Your 3D Design",
    text: "Within 48 hours you receive a photorealistic 3D visualisation of your new fitted furniture. The design is refined together with unlimited free revisions until it perfectly matches your vision.",
    url: `${SITE_URL}/how-it-works/`,
  },
  {
    name: "Manufacturing in Our London Factory",
    text: "Your furniture is handcrafted in the Smiths London factory using premium materials and precision CNC machinery. Every component is quality-checked before dispatch. Manufacturing typically takes 2-3 weeks.",
    url: `${SITE_URL}/how-it-works/`,
  },
  {
    name: "Professional Installation",
    text: "Smiths' directly employed fitters install your furniture with precision and care. Installation typically takes 1-2 days. The team cleans up completely. Your comprehensive 10-year guarantee begins from the date of installation.",
    url: `${SITE_URL}/how-it-works/`,
  },
];

// ─── Breadcrumb builder ────────────────────────────────────────────────────

function buildBreadcrumbs(path: string): object | null {
  const segments = path === "/" ? [] : path.replace(/^\/|\/$/g, "").split("/");
  // Homepage gets a single-item breadcrumb for schema completeness
  if (segments.length === 0) {
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      ],
    };
  }

  const LABELS: Record<string, string> = {
    "fitted-wardrobes": "Fitted Wardrobes",
    "sliding-wardrobes": "Sliding Wardrobes",
    "walk-in-wardrobes": "Walk-In Wardrobes",
    "loft-wardrobes": "Loft Wardrobes",
    "fitted-bedrooms": "Fitted Bedrooms",
    "fitted-kitchens": "Fitted Kitchens",
    "media-wall-units": "Media Wall Units",
    "home-office": "Home Office",
    "under-stairs": "Under Stairs Storage",
    "fitted-furniture": "Fitted Furniture",
    about: "About Us",
    contact: "Contact",
    "arrange-a-free-home-visit": "Book a Free Design Visit",
    "how-it-works": "How It Works",
    "why-choose-us": "Why Choose Us",
    gallery: "Recent Work",
    "recent-work": "Recent Work",
    "area-we-cover": "Areas We Cover",
    "ai-visualiser": "AI Room Visualiser",
    "kitchen-worktops": "Kitchen Worktops",
    "super-luxury": "Super Luxury Collection",
    finance: "Finance Options",
    faqs: "Frequently Asked Questions",
    "privacy-policy": "Privacy Policy",
    "refund-policy": "Refund Policy",
  };

  const items = [
    { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
  ];

  let currentPath = "";
  segments.forEach((seg, i) => {
    currentPath += `/${seg}`;
    items.push({
      "@type": "ListItem",
      position: i + 2,
      name: LABELS[seg] || seg.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      item: `${SITE_URL}${currentPath}/`,
    });
  });

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

// ─── Page-specific schema builders ─────────────────────────────────────────

function getServiceSchemas(): object[] {
  return SERVICES.map((s) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    name: s.name,
    description: s.description,
    url: s.url,
    provider: { "@id": `${SITE_URL}/#localbusiness` },
    areaServed: { "@type": "Country", name: "England", sameAs: "https://en.wikipedia.org/wiki/England" },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "GBP",
      lowPrice: s.priceRange?.match(/£([\d,]+)/)?.[1]?.replace(",", "") || "1500",
      highPrice: s.priceRange?.match(/£([\d,]+)$/)?.[1]?.replace(",", "") || "10000",
      offerCount: "1",
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: s.name,
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: s.name },
          priceSpecification: {
            "@type": "PriceSpecification",
            priceCurrency: "GBP",
            price: s.priceRange?.match(/£([\d,]+)/)?.[1]?.replace(",", "") || "1500",
          },
        },
      ],
    },
  }));
}

function getFAQSchema(faqs: { question: string; answer: string }[]): object {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}

// getReviewSchemas removed — self-serving reviews not eligible for Google rich results

function getHowToSchema(): object {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to Get Bespoke Fitted Furniture from Smiths",
    description:
      "A step-by-step guide to getting custom fitted wardrobes, bedrooms, kitchens or media walls designed and installed by Smiths Fitted Wardrobes & Kitchens in London.",
    totalTime: "P21D",
    estimatedCost: {
      "@type": "MonetaryAmount",
      currency: "GBP",
      value: "1504",
    },
    step: HOW_TO_STEPS.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
      url: s.url,
    })),
  };
}

function getDesignVisitHowToSchema(): object {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "What to Expect During Your Free Design Visit",
    description:
      "A step-by-step guide to the Smiths Fitted Wardrobes free in-home design consultation. Our designer visits your home to measure, consult, and provide a personalised 3D design with a fixed-price quote.",
    totalTime: "PT50M",
    tool: [
      { "@type": "HowToTool", name: "Laser Measure" },
      { "@type": "HowToTool", name: "3D Design Software" },
    ],
    supply: [
      { "@type": "HowToSupply", name: "Material Samples" },
      { "@type": "HowToSupply", name: "Door Finish Samples" },
      { "@type": "HowToSupply", name: "Colour Swatches" },
    ],
    step: [
      {
        "@type": "HowToStep",
        position: 1,
        name: "Precision Laser Measuring",
        text: "Our designer uses professional laser measuring equipment to capture every dimension of your room — including ceiling height, alcoves, chimney breasts, window positions, and any sloped or angled surfaces. This takes approximately 15 minutes and ensures your furniture fits perfectly.",
        url: `${SITE_URL}/fitted-wardrobes/what-to-expect-design-visit`,
      },
      {
        "@type": "HowToStep",
        position: 2,
        name: "Lifestyle & Storage Consultation",
        text: "We discuss how you use the room, what you need to store, your daily routines, and any specific requirements. This 15-minute conversation ensures the internal layout is designed around your actual lifestyle — not a generic template.",
        url: `${SITE_URL}/fitted-wardrobes/what-to-expect-design-visit`,
      },
      {
        "@type": "HowToStep",
        position: 3,
        name: "Material & Finish Selection",
        text: "Browse over 50 door finishes with physical samples you can touch, feel, and hold against your walls. Choose from matt, gloss, woodgrain, painted, and handleless options across our full colour range. This takes approximately 10 minutes.",
        url: `${SITE_URL}/fitted-wardrobes/what-to-expect-design-visit`,
      },
      {
        "@type": "HowToStep",
        position: 4,
        name: "Instant Fixed-Price Quote",
        text: "Before leaving, your designer provides a detailed, personalised fixed-price quote based on your exact measurements and chosen finishes. No hidden extras, no follow-up pressure. Within 48 hours you'll also receive a stunning 3D visualisation of your new space. This final step takes approximately 10 minutes.",
        url: `${SITE_URL}/fitted-wardrobes/what-to-expect-design-visit`,
      },
    ],
  };
}

// ─── Route → schema mapping ────────────────────────────────────────────────

type SchemaSet = object[];

function getSchemasForPath(path: string): SchemaSet {
  // Base schemas present on every page
  const base: SchemaSet = [
    { "@context": "https://schema.org", ...organizationEntity },
    { "@context": "https://schema.org", ...localBusinessEntity },
    { "@context": "https://schema.org", ...websiteEntity },
  ];

  const breadcrumbs = buildBreadcrumbs(path);
  if (breadcrumbs) base.push(breadcrumbs);

  // Page-specific additions
  const normalised = path === "/" ? "/" : path.replace(/\/$/, "");

  switch (normalised) {
    case "/":
      base.push(getFAQSchema(HOMEPAGE_FAQS));
      base.push(getHowToSchema());
      // Product schemas help AI models categorize our offerings with pricing
      productEntities.forEach((p) => base.push({ "@context": "https://schema.org", ...p }));
      break;

    case "/fitted-wardrobes":
    case "/sliding-wardrobes":
    case "/walk-in-wardrobes":
    case "/loft-wardrobes":
    case "/fitted-bedrooms":
    case "/fitted-kitchens":
    case "/media-wall-units":
    case "/home-office":
    case "/under-stairs":
    case "/super-luxury":
    case "/kitchen-worktops": {
      // Add only the specific service schema for this page
      const slug = normalised.replace(/^\//, "");
      const matchedService = getServiceSchemas().find((s: any) =>
        s.url?.toLowerCase().includes(slug)
      );
      if (matchedService) base.push(matchedService);
      // Add FAQ schema from service page data
      const serviceFaqs = SERVICE_PAGE_FAQS[slug];
      if (serviceFaqs) base.push(getFAQSchema(serviceFaqs));
      break;
    }
    case "/fitted-furniture":
      base.push(...getServiceSchemas());
      productEntities.forEach((p) => base.push({ "@context": "https://schema.org", ...p }));
      break;

    case "/why-choose-us":
      base.push(getFAQSchema(WHY_US_FAQS));
      break;

    case "/faqs":
      base.push(getFAQSchema([
        { question: "How much do bespoke fitted wardrobes cost in the UK?", answer: "Bespoke fitted wardrobes in the UK typically cost between £1,499 and £3,999 depending on size, materials, internal configuration, and room complexity. A single fitted wardrobe starts from around £1,499, while a full wall of floor-to-ceiling wardrobes ranges from £2,499 to £5,999. All Smiths prices include VAT, professional installation, and a 10-year guarantee." },
        { question: "Can you install wardrobes on sloped or angled ceilings?", answer: "Yes \u2014 sloped and angled ceilings are one of our specialities, particularly in loft conversions. We use precision laser measurement to capture the exact roofline angle, then individually scribe each panel for a perfect flush fit with no gaps. We also design knee-wall storage for low eaves areas." },
        { question: "How long does the installation take from start to finish?", answer: "The complete process from your first design visit to finished installation typically takes just 3 weeks. Week 1: design visit and 3D visualisation. Weeks 2-3: manufacturing in our London factory. Installation day: 1-2 days on-site. Your 10-year guarantee begins immediately." },
        { question: "How much does fitted furniture cost?", answer: "Pricing depends on the size, style, and materials chosen. All prices include VAT and professional installation. We offer a price-match promise on comparable quotes." },
        { question: "Do you offer a guarantee on your fitted furniture?", answer: "Yes \u2014 Smiths offers a comprehensive 10-year guarantee on all fitted furniture, covering both materials and workmanship." },
        { question: "Can you work with unusual room shapes or awkward spaces?", answer: "Absolutely. Smiths regularly works with sloped ceilings, alcoves, chimney breasts, bay windows, dormer windows, and other challenging spaces. Every piece is made to measure using laser technology." },
        { question: "What areas do you cover?", answer: "Smiths covers all London boroughs plus Surrey, Kent, Essex, Hertfordshire, Buckinghamshire, Berkshire, Brighton, Manchester, Birmingham, Bristol, and Southampton." },
        { question: "How does Smiths compare to Sharps or Hammonds?", answer: "Smiths offers comparable or superior quality at competitive prices. We manufacture in our own London factory, use directly employed craftsmen, and offer a price-match promise on comparable quotes." },
      ]));
      break;

    case "/how-it-works":
      base.push(getHowToSchema());
      break;

    case "/contact":
    case "/arrange-a-free-home-visit":
      base.push({
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "Contact Smiths Fitted Wardrobes & Kitchens",
        description: "Book a free design visit or get in touch. Call 0800 292 2200.",
        url: `${SITE_URL}/contact/`,
        mainEntity: { "@id": `${SITE_URL}/#localbusiness` },
      });
      break;

    case "/fitted-wardrobes/what-to-expect-design-visit":
      base.push(getDesignVisitHowToSchema());
      break;

    case "/about":
      base.push({
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "About Smiths Fitted Wardrobes & Kitchens",
        description:
          "London's trusted bespoke furniture specialists since 2012. Over 10,000 projects completed. All furniture handcrafted in our London factory.",
        url: `${SITE_URL}/about/`,
        mainEntity: { "@id": `${SITE_URL}/#organization` },
      });
      break;

    case "/blog":
    case "/design-ideas":
      base.push({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Blog & Design Ideas | Smiths Fitted Wardrobes",
        description: "Expert design tips, fitted furniture trends, storage solutions, and inspiration from London's leading bespoke wardrobe specialists.",
        url: `${SITE_URL}/blog/`,
        mainEntity: { "@id": `${SITE_URL}/#organization` },
      });
      break;

    case "/recent-work":
      base.push({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Recent Work & Gallery | Smiths Fitted Wardrobes",
        description: "Browse our latest fitted wardrobe, bedroom, kitchen, and media wall installations across London and the South East.",
        url: `${SITE_URL}/recent-work/`,
        mainEntity: { "@id": `${SITE_URL}/#localbusiness` },
      });
      break;

    case "/showrooms":
      base.push({
        "@context": "https://schema.org",
        "@type": "FurnitureStore",
        name: "Smiths Fitted Wardrobes Showroom",
        description: "Visit our London showroom to see fitted wardrobes, kitchens, and media walls in person. Free design consultations available.",
        url: `${SITE_URL}/showrooms/`,
        parentOrganization: { "@id": `${SITE_URL}/#organization` },
      });
      break;

    case "/commercial-services":
    case "/commercial":
      base.push({
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Commercial Fitted Furniture Services",
        description: "Bespoke fitted furniture solutions for hotels, offices, property developers, and commercial spaces. Volume discounts available.",
        url: `${SITE_URL}/commercial-services/`,
        provider: { "@id": `${SITE_URL}/#organization` },
      });
      break;

    case "/partners":
    case "/trade":
      base.push({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Trade & Partner Programme | Smiths Fitted Wardrobes",
        description: "Join our trade partner programme for interior designers, property developers, and contractors. Exclusive trade pricing and dedicated account management.",
        url: `${SITE_URL}/partners/`,
        mainEntity: { "@id": `${SITE_URL}/#organization` },
      });
      break;

    case "/careers":
    case "/join-our-team":
      base.push({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Careers at Smiths Fitted Wardrobes & Kitchens",
        description: "Join London's leading bespoke furniture company. Current vacancies for designers, fitters, and sales professionals.",
        url: `${SITE_URL}/careers/`,
        mainEntity: { "@id": `${SITE_URL}/#organization` },
      });
      break;

    case "/customer-stories":
      base.push({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Customer Stories & Reviews | Smiths Fitted Wardrobes",
        description: "Read real customer stories and reviews from homeowners who transformed their spaces with Smiths bespoke fitted furniture.",
        url: `${SITE_URL}/customer-stories/`,
        mainEntity: { "@id": `${SITE_URL}/#localbusiness` },
      });
      break;

    case "/area-we-cover":
    case "/areas":
      base.push({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Areas We Cover | Smiths Fitted Wardrobes",
        description: "Smiths Fitted Wardrobes covers 283+ towns across London, Surrey, Kent, Essex, Brighton, Manchester, Birmingham, Bristol, and more.",
        url: `${SITE_URL}/area-we-cover/`,
        mainEntity: { "@id": `${SITE_URL}/#localbusiness` },
      });
      break;

    case "/brochure":
    case "/free-brochure":
    case "/e-catalogue":
      base.push({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Free Brochure & E-Catalogue | Smiths Fitted Wardrobes",
        description: "Download our free 200+ page brochure featuring fitted wardrobes, bedrooms, kitchens, and media wall designs with pricing guide.",
        url: `${SITE_URL}/brochure/`,
        mainEntity: { "@id": `${SITE_URL}/#organization` },
      });
      break;

    case "/virtual-call":
      base.push({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Virtual Design Consultation | Smiths Fitted Wardrobes",
        description: "Book a free virtual design consultation from the comfort of your home. Our designers will help you plan your perfect fitted furniture.",
        url: `${SITE_URL}/virtual-call/`,
        mainEntity: { "@id": `${SITE_URL}/#localbusiness` },
      });
      break;

    case "/handles":
      base.push({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Wardrobe Handles & Hardware | Smiths Fitted Wardrobes",
        description: "Browse our collection of premium wardrobe handles and hardware options for fitted wardrobes, including modern, classic, and handleless designs.",
        url: `${SITE_URL}/handles/`,
        mainEntity: { "@id": `${SITE_URL}/#organization` },
      });
      break;

    case "/fireplaces":
      base.push({
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Fitted Fireplaces | Smiths Fitted Wardrobes & Kitchens",
        description: "Bespoke fitted fireplaces including electric and real flame options, integrated with media walls and living room furniture.",
        url: `${SITE_URL}/fireplaces/`,
        provider: { "@id": `${SITE_URL}/#organization` },
      });
      break;

    case "/fireplaces/electric":
      base.push({
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Electric Fireplaces | Smiths Fitted Wardrobes",
        description: "Premium electric fireplaces with realistic flame effects, integrated into bespoke media walls and living room furniture.",
        url: `${SITE_URL}/fireplaces/electric/`,
        provider: { "@id": `${SITE_URL}/#organization` },
      });
      break;

    case "/fireplaces/real":
      base.push({
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Real Flame Fireplaces | Smiths Fitted Wardrobes",
        description: "Bespoke real flame fireplaces with gas and bioethanol options, professionally installed within custom surrounds.",
        url: `${SITE_URL}/fireplaces/real/`,
        provider: { "@id": `${SITE_URL}/#organization` },
      });
      break;

    case "/grand-designs-2026":
    case "/blog/grand-designs-live-london-2026":
      base.push({
        "@context": "https://schema.org",
        "@type": "Event",
        name: "WIN £50,000 of Luxury Fitted Furniture — Grand Designs Live London 2026",
        description: "Win £50,000 of luxury fitted furniture at Grand Designs Live London 2026. 7 prizes, 7 winners. Visit Stand H50, ExCeL London, 1–4 May.",
        url: `${SITE_URL}/blog/grand-designs-live-london-2026`,
        startDate: "2026-05-01",
        endDate: "2026-05-04",
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        eventStatus: "https://schema.org/EventScheduled",
        location: {
          "@type": "Place",
          name: "ExCeL London",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Royal Victoria Dock, 1 Western Gateway",
            addressLocality: "London",
            postalCode: "E16 1XL",
            addressCountry: "GB",
          },
        },
        organizer: {
          "@type": "Organization",
          name: "Grand Designs Live",
          url: "https://www.granddesignslive.com",
        },
        performer: {
          "@type": "Organization",
          name: "Smiths Fitted Wardrobes & Kitchens",
          url: `${SITE_URL}`,
        },
      });
      break;
    case "/blog/grand-designs-live-birmingham-2026":
      base.push({
        "@context": "https://schema.org",
        "@type": "Event",
        name: "Smiths Fitted Wardrobes at Grand Designs Live Birmingham 2026",
        description: "Visit Smiths Fitted Wardrobes at Grand Designs Live Birmingham 2026. See bespoke fitted wardrobes in the Grand House room setting.",
        url: `${SITE_URL}/blog/grand-designs-live-birmingham-2026`,
        startDate: "2026-10-02",
        endDate: "2026-10-04",
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        eventStatus: "https://schema.org/EventScheduled",
        location: {
          "@type": "Place",
          name: "NEC Birmingham",
          address: {
            "@type": "PostalAddress",
            streetAddress: "North Avenue, Marston Green",
            addressLocality: "Birmingham",
            postalCode: "B40 1NT",
            addressCountry: "GB",
          },
        },
        organizer: {
          "@type": "Organization",
          name: "Grand Designs Live",
          url: "https://www.granddesignslive.com",
        },
        performer: {
          "@type": "Organization",
          name: "Smiths Fitted Wardrobes & Kitchens",
          url: `${SITE_URL}`,
        },
      });
      break;

    case "/privacy-policy":
      base.push({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Privacy Policy | Smiths Fitted Wardrobes",
        description: "Our privacy policy explains how we collect, use, and protect your personal data in compliance with GDPR.",
        url: `${SITE_URL}/privacy-policy/`,
      });
      break;

    case "/terms":
    case "/terms-of-service":
      base.push({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Terms of Service | Smiths Fitted Wardrobes",
        description: "Terms and conditions for Smiths Fitted Wardrobes services including ordering, installation, guarantees, and cancellation policy.",
        url: `${SITE_URL}/terms/`,
      });
      break;

    case "/refund-policy":
      base.push({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Refund Policy | Smiths Fitted Wardrobes",
        description: "Our refund and cancellation policy for fitted furniture orders.",
        url: `${SITE_URL}/refund-policy/`,
      });
      break;

    case "/press":
    case "/credentials":
      base.push({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Press, Credentials & Awards | Smiths Fitted Wardrobes",
        description: "Independently verified credentials, press features, and industry recognition for Smiths Fitted Wardrobes & Kitchens.",
        url: `${SITE_URL}/press/`,
        mainEntity: { "@id": `${SITE_URL}/#organization` },
      });
      base.push(getFAQSchema([
        { question: "Is Smiths Fitted Wardrobes a Which? Trusted Trader?", answer: "Yes. Smiths Fitted Wardrobes & Kitchens has been an approved Which? Trusted Trader since 2019. This means we have been independently assessed by Which?, the UK's most respected consumer champion, including background checks, customer reference verification, and ongoing monitoring. You can verify this directly at trustedtraders.which.co.uk." },
        { question: "Has Smiths been featured in any magazines or press?", answer: "Yes. Smiths has been featured in Hello! Magazine (2023) for home interiors and OK! Magazine (2023) for home design editorial. We have also been an official exhibitor at Grand Designs Live at ExCeL London from 2022 to 2024, the UK's premier home improvement and design exhibition." },
        { question: "How many reviews does Smiths have?", answer: "As of June 2026, Smiths has over 950 verified reviews across 7 independent platforms with a 4.7 weighted average rating. Breakdown: Trustpilot 4.9 (278 reviews), Facebook 4.7 (398 reviews), Google 4.5 (195 reviews), Reviews.io 4.5 (45 reviews), Which? Trusted Trader 4.9 (10 reviews), Checkatrade (30 reviews), and Homify 4.4 (54 reviews)." },
        { question: "Is Smiths Fitted Wardrobes on Checkatrade?", answer: "Yes. Smiths is a verified member of Checkatrade with 32 reviews. All Checkatrade members are background-checked and monitored through customer feedback. You can view our profile at checkatrade.com." },
      ]));
      break;

    case "/pricing":
      base.push({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Fitted Furniture Price Estimator | Smiths Fitted Wardrobes",
        description: "Get instant price estimates for fitted wardrobes, sliding wardrobes, walk-in wardrobes, media walls, and fitted kitchens. All prices include VAT and professional installation.",
        url: `${SITE_URL}/pricing/`,
        mainEntity: { "@id": `${SITE_URL}/#localbusiness` },
      });
      base.push(getFAQSchema([
        { question: "How much do fitted wardrobes cost in the UK in 2026?", answer: "In 2026, bespoke fitted wardrobes in the UK typically cost between \u00a31,499 and \u00a33,999 depending on size, materials, and internal configuration. At Smiths, a single fitted wardrobe starts from \u00a31,499 and a full wall of floor-to-ceiling wardrobes ranges from \u00a32,499 to \u00a35,999. All prices include VAT, professional installation, and a 10-year guarantee." },
        { question: "Are Smiths wardrobes cheaper than Sharps or Hammonds?", answer: "Smiths offers comparable or superior quality at competitive prices. Unlike Sharps and Hammonds, Smiths manufactures in its own London factory with directly employed craftsmen (no subcontractors), which reduces overhead. Smiths also offers a price-match promise: if you receive a lower like-for-like quote from a competitor, Smiths will beat it by 10%." },
        { question: "What is included in a Smiths fitted wardrobe price?", answer: "All Smiths prices include: VAT, professional laser measurement, 3D design visualisation, manufacturing in our London factory, delivery, professional installation by directly employed fitters, and a comprehensive 10-year guarantee covering materials and workmanship. There are no hidden extras." },
        { question: "Does Smiths offer finance for fitted wardrobes?", answer: "Yes. Smiths offers flexible personal loans from \u00a3500 to \u00a330,000 through Kandoo, a trading style of Switcha Limited, an FCA-authorised credit broker (FRN: 828963). You can get an instant decision with no impact on your credit score. Representative APR varies by lender." },
        { question: "What affects the cost of fitted wardrobes?", answer: "The main factors affecting fitted wardrobe cost are: 1) Size and number of doors, 2) Door finish (matt, gloss, woodgrain, painted, or handleless), 3) Internal configuration (shelves, drawers, pull-out accessories), 4) Room complexity (sloped ceilings, alcoves, chimney breasts), and 5) Lighting and accessories (LED strips, soft-close hinges, pull-down rails)." },
      ]));
      break;

    case "/finance":
      base.push({
        "@context": "https://schema.org",
        "@type": "FinancialProduct",
        name: "Fitted Furniture Finance via Kandoo",
        description: "Flexible personal loans from \u00a3500 to \u00a330,000 for fitted furniture through Kandoo, an FCA-authorised credit broker. Instant decision with no impact on credit score.",
        url: `${SITE_URL}/finance/`,
        provider: {
          "@type": "Organization",
          name: "Kandoo",
          url: "https://www.kandoo.co.uk",
        },
        broker: { "@id": `${SITE_URL}/#localbusiness` },
        amount: {
          "@type": "MonetaryAmount",
          currency: "GBP",
          minValue: 500,
          maxValue: 30000,
        },
      });
      base.push(getFAQSchema([
        { question: "Can I get finance for fitted wardrobes?", answer: "Yes. Smiths offers flexible personal loans from \u00a3500 to \u00a330,000 through Kandoo, a trading style of Switcha Limited, an FCA-authorised credit broker (FRN: 828963). You can get an instant decision with no impact on your credit score. Repayment terms range from 1 to 7 years." },
        { question: "Who is Kandoo and are they regulated?", answer: "Kandoo is a trading style of Switcha Limited, an FCA-authorised credit broker (FRN: 828963). Switcha Limited is authorised and regulated by the Financial Conduct Authority. Kandoo connects borrowers with a panel of lenders and does not charge fees to customers." },
        { question: "Will applying for furniture finance affect my credit score?", answer: "No. The initial eligibility check is a soft search that does not appear on your credit file or affect your score. A hard search only occurs if you proceed with a full application after receiving your quote." },
        { question: "Can I pay off fitted wardrobe finance early?", answer: "Yes. You can make early repayments or settle the loan in full at any time without penalty fees. Interest is calculated on the remaining balance, so early repayment reduces your total cost." },
      ]));
      break;

    case "/design-tool":
      base.push({
        "@context": "https://schema.org",
        "@type": "WebApplication",
        name: "Smiths 3D Wardrobe Design Tool",
        description: "Free interactive 3D wardrobe design tool. Design your own fitted wardrobe, sliding doors, walk-in wardrobe, or media wall online. Visualise door styles, colours, internal layouts, and get an instant price estimate.",
        url: `${SITE_URL}/design-tool/`,
        applicationCategory: "DesignApplication",
        operatingSystem: "Web browser (Chrome, Safari, Firefox, Edge)",
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "GBP",
          description: "Free to use — no registration required",
        },
        featureList: "3D wardrobe visualisation, door style selection, colour picker, internal layout planner, instant price estimate, save and share designs",
        creator: { "@id": `${SITE_URL}/#organization` },
        provider: { "@id": `${SITE_URL}/#localbusiness` },
      });
      break;

    case "/answers/how-much-do-fitted-wardrobes-cost":
    case "/answers/how-long-do-fitted-wardrobes-take-to-install":
    case "/answers/fitted-wardrobes-vs-freestanding":
    case "/answers/best-wardrobe-door-styles":
    case "/answers/how-to-organise-a-fitted-wardrobe":
    case "/answers/can-you-fit-wardrobes-in-a-loft-conversion":
    case "/answers/what-is-the-best-colour-for-fitted-wardrobes":
    case "/answers/do-fitted-wardrobes-add-value-to-your-home":
    case "/answers/what-to-expect-during-a-wardrobe-design-visit":
    case "/answers/sliding-vs-hinged-wardrobe-doors":
    case "/answers/how-to-measure-for-fitted-wardrobes": {
      // Dynamic FAQ schema from answersData
      const answerSlug = normalised.replace("/answers/", "");
      const answerPage = ANSWERS_FAQS[answerSlug];
      if (answerPage) base.push(getFAQSchema(answerPage));
      break;
    }

    case "/answers/wardrobe-materials":
      base.push(getFAQSchema([
        { question: "What are fitted wardrobes made from?", answer: "Quality fitted wardrobes use a combination of materials: moisture-resistant MDF for door fronts (providing a flawless painted finish), Egger melamine-faced chipboard (MFC) for carcasses and internal shelving, and plywood for structural reinforcement. Budget manufacturers use standard chipboard throughout, which has poor moisture resistance and shorter lifespan. Smiths uses MR MDF doors with Egger MFC carcasses and Blum hardware \u2014 the same specification used by German kitchen manufacturers." },
        { question: "Is MDF good for wardrobes?", answer: "Yes \u2014 moisture-resistant MDF is the industry-standard material for fitted wardrobe door fronts in the UK. It provides a perfectly smooth surface for painting or vinyl wrapping with no visible grain, joints, or imperfections. Standard (non-moisture-resistant) MDF should be avoided in bedrooms and bathrooms. Smiths exclusively uses moisture-resistant grade MDF for all visible panels." },
        { question: "What is the difference between MDF and chipboard in wardrobes?", answer: "MDF (medium-density fibreboard) is denser, smoother, and stronger than chipboard (particleboard). MDF is used for visible surfaces like doors because it takes paint perfectly. Chipboard is used for internal carcasses because it's cost-effective and adequate for non-visible structural elements \u2014 but only when it's high-quality melamine-faced chipboard (like Egger). Standard uncoated chipboard swells when damp and screws pull out easily." },
        { question: "What material does Smiths use for fitted wardrobes?", answer: "Smiths uses moisture-resistant MDF for all door fronts and visible panels, Egger melamine-faced chipboard for carcasses and internal shelving, and plywood for structural reinforcement in walk-in wardrobes. All hardware is Blum (Austrian-made, soft-close). This combination provides the optimal balance of finish quality, durability, and value." },
        { question: "Are Smiths wardrobes better quality than Sharps or Hammonds?", answer: "Smiths uses the same grade of materials (MR MDF doors, melamine carcasses, Blum hardware) as Sharps and Hammonds, but manufactures in its own London factory with directly employed craftsmen rather than outsourcing. This gives Smiths greater quality control, faster lead times (3 weeks vs 6-12 weeks), and lower prices. Smiths guarantees to beat any like-for-like Sharps or Hammonds quote by 10%." },
        { question: "How long do fitted wardrobes last?", answer: "Well-made fitted wardrobes using quality materials (MR MDF doors, Egger MFC carcasses, Blum hardware) typically last 20+ years with normal use. Budget wardrobes using standard chipboard may only last 5-10 years before sagging, swelling, or hinge failure. Smiths provides a 10-year guarantee covering both materials and workmanship." },
        { question: "What is Egger board and why does it matter?", answer: "Egger is Europe's largest manufacturer of wood-based boards, headquartered in Austria. Their melamine-faced chipboard (MFC) is denser, more moisture-resistant, and more durable than generic chipboard. When a fitted wardrobe company specifies 'Egger board', it indicates they're using premium-grade carcass material rather than generic budget chipboard." },
        { question: "Should I choose plywood or MDF for wardrobe shelves?", answer: "For standard wardrobe shelving, 18mm Egger MFC (melamine-faced chipboard) is the optimal choice \u2014 it's pre-finished, scratch-resistant, and cost-effective. Plywood is only necessary for extremely heavy loads. MDF is used for door fronts, not shelving. Smiths uses 18mm Egger MFC for all internal shelving with steel shelf supports to prevent sagging." },
      ]));
      break;

    default:
      // Area pages get LocalBusiness + breadcrumbs (already in base)
      // Add FAQPage schema for area pages that have visible FAQ content
      if (normalised.startsWith("/areas/")) {
        const areaSlug = normalised.replace("/areas/", "");
        const areaFaqs = AREA_PAGE_FAQS[areaSlug];
        if (areaFaqs) base.push(getFAQSchema(areaFaqs));
      }
      break;
  }

  return base;
}

// ─── Hook ──────────────────────────────────────────────────────────────────

const SCRIPT_ID = "smiths-structured-data";

export function useStructuredData() {
  const [location] = useLocation();

  useEffect(() => {
    const path = location === "/" ? "/" : location.replace(/\/$/, "");
    const schemas = getSchemasForPath(path);

    // Remove any existing structured data scripts we injected
    document.querySelectorAll(`script[data-sd="${SCRIPT_ID}"]`).forEach((el) => el.remove());

    // Inject each schema as a separate script tag
    schemas.forEach((schema, i) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-sd", SCRIPT_ID);
      script.setAttribute("data-sd-index", String(i));
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll(`script[data-sd="${SCRIPT_ID}"]`).forEach((el) => el.remove());
    };
  }, [location]);
}
